import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
    Play,
    Eye,
    Share2,
    TrendingUp,
    Calendar,
    Award,
    Flame,
    Clock,
    Trophy,
    User
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// --- COMPONENTS ---

const RankBadge = ({ rank }) => {
    if (rank === 1) {
        return (
            <div className="absolute -top-3 -left-3 z-20 w-8 h-8 rounded-full bg-yellow-500 border-2 border-background flex items-center justify-center shadow-[0_0_15px_rgba(234,179,8,0.6)] animate-pulse">
                <Trophy className="w-4 h-4 text-black fill-current" />
            </div>
        );
    }
    if (rank === 2) {
        return (
            <div className="absolute -top-3 -left-3 z-20 w-8 h-8 rounded-full bg-slate-300 border-2 border-background flex items-center justify-center shadow-lg">
                <span className="font-heading text-black font-bold text-sm">#2</span>
            </div>
        );
    }
    if (rank === 3) {
        return (
            <div className="absolute -top-3 -left-3 z-20 w-8 h-8 rounded-full bg-orange-700 border-2 border-background flex items-center justify-center shadow-lg">
                <span className="font-heading text-white font-bold text-sm">#3</span>
            </div>
        );
    }
    return (
        <div className="absolute -top-2 -left-2 z-20 w-6 h-6 rounded-full bg-black/80 border border-white/20 flex items-center justify-center backdrop-blur">
            <span className="font-mono text-xs text-muted-foreground">#{rank}</span>
        </div>
    );
};

const ViralCard = ({ session, rank, onShare }) => {
    const navigate = useNavigate();

    // Special styling for top 3
    const isTop3 = rank <= 3;
    const glowClass = rank === 1 ? 'glow-gold' : (rank === 2 ? 'glow-silver' : (rank === 3 ? 'glow-bronze' : ''));
    const borderClass = rank === 1 ? 'border-yellow-500/50' : '';

    return (
        <div className={`card-base relative group transition-all duration-300 hover:scale-[1.02] ${borderClass} ${glowClass}`}>
            <RankBadge rank={rank} />

            {/* Thumbnail */}
            <div
                className="aspect-video bg-muted relative cursor-pointer rounded-t-xl overflow-hidden"
                onClick={() => navigate(`/watch/${session.id}`)}
            >
                {session.clip?.thumbnail_url ? (
                    <img
                        src={session.clip.thumbnail_url}
                        alt={session.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-card">
                        <Play className="w-10 h-10 text-muted-foreground" />
                    </div>
                )}

                {/* Overlay: Play Button */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-primary text-black flex items-center justify-center transform scale-0 group-hover:scale-100 transition-transform duration-300 delay-75">
                        <Play className="w-6 h-6 fill-current" />
                    </div>
                </div>

                {/* Subtitle Preview (Bottom) */}
                <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/90 to-transparent translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <p className="text-xs text-white/90 line-clamp-2 italic font-medium">
                        "{session.subtitle_data?.[0]?.text?.replace(/<[^>]+>/g, '') || 'Watch to see captions...'}"
                    </p>
                </div>
            </div>

            {/* Info */}
            <div className="p-3">
                <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="font-heading text-sm uppercase truncate flex-1 hover:text-primary cursor-pointer" onClick={() => navigate(`/watch/${session.id}`)}>
                        {session.title}
                    </h3>
                </div>

                <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                    {/* User Avatar & Name */}
                    <span className="flex items-center gap-2">
                        {session.creator_avatar_url ? (
                            <img
                                src={session.creator_avatar_url}
                                alt={session.creator_username}
                                className="w-5 h-5 rounded-full object-cover border border-border"
                            />
                        ) : (
                            <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center">
                                <User className="w-3 h-3 text-muted-foreground" />
                            </div>
                        )}

                        {session.creator_username ? (
                            <span className="text-primary font-medium truncate max-w-[100px]">
                                @{session.creator_username}
                            </span>
                        ) : 'Anonymous'}
                    </span>

                    {/* View Count */}
                    <span className="flex items-center gap-1 font-mono">
                        <Eye className="w-3 h-3" />
                        {session.views.toLocaleString()}
                    </span>
                </div>

                <Button
                    variant="outline"
                    size="sm"
                    className="w-full h-8 text-xs gap-2 hover:bg-primary hover:text-black transition-colors"
                    onClick={() => onShare(session)}
                >
                    <Share2 className="w-3 h-3" />
                    Share
                </Button>
            </div>
        </div>
    );
};

export default function Leaderboard() {
    const navigate = useNavigate();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters
    const [sort, setSort] = useState('popular'); // trending, popular, newest
    const [timeRange, setTimeRange] = useState('all'); // 24h, 7d, 30d, all

    useEffect(() => {
        fetchLeaderboard();
    }, [sort, timeRange]);

    const fetchLeaderboard = async () => {
        setLoading(true);
        try {
            // Fetch top 50 clips based on current filters
            const response = await axios.get(`${API}/discovery`, {
                params: {
                    sort: sort,
                    time_range: timeRange,
                    limit: 50
                }
            });
            setSessions(response.data);
        } catch (error) {
            console.error('Failed to fetch leaderboard:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleShare = (session) => {
        // Grab the backend URL from environment variables
        const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

        // Build the URL pointing to the backend Traffic Cop
        const url = `${backendUrl}/api/share/${session.id}`;

        navigator.clipboard.writeText(url);
        // Simple toast trigger (assuming sonner is setup globally)
        // Or you can create a simple alert
        alert('Link copied to clipboard!');
    };

    return (
        <Layout>
            <div className="min-h-screen py-12 relative overflow-hidden">
                {/* Background Ambient Glow */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/5 blur-[120px] rounded-full pointer-events-none" />

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

                    {/* Header */}
                    <div className="text-center mb-10">
                        <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-full mb-4 animate-slide-up">
                            <Trophy className="w-8 h-8 text-primary" />
                        </div>
                        <h1 className="font-brand text-4xl md:text-6xl uppercase mb-4 animate-slide-up" style={{ animationDelay: '100ms' }}>
                            SubHack <span className="text-primary">Hall of Fame</span>
                        </h1>
                        <p className="text-muted-foreground max-w-xl mx-auto animate-slide-up" style={{ animationDelay: '200ms' }}>
                            The most viral, most hilarious, and most shared clips on the platform.
                            Create yours to join the ranks.
                        </p>
                    </div>

                    {/* Controls Bar */}
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8 bg-card/50 border border-border p-2 rounded-xl backdrop-blur animate-slide-up" style={{ animationDelay: '300ms' }}>

                        {/* Sort Toggles */}
                        <div className="flex p-1 bg-muted rounded-lg">
                            <button
                                onClick={() => { setSort('trending'); setTimeRange('7d'); }}
                                className={`px-4 py-2 rounded-md text-sm font-bold flex items-center gap-2 transition-all ${sort === 'trending' ? 'bg-background text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                            >
                                <Flame className="w-4 h-4" />
                                Trending
                            </button>
                            <button
                                onClick={() => { setSort('popular'); setTimeRange('all'); }}
                                className={`px-4 py-2 rounded-md text-sm font-bold flex items-center gap-2 transition-all ${sort === 'popular' ? 'bg-background text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                            >
                                <Award className="w-4 h-4" />
                                Most Viewed
                            </button>
                            <button
                                onClick={() => { setSort('newest'); setTimeRange('all'); }}
                                className={`px-4 py-2 rounded-md text-sm font-bold flex items-center gap-2 transition-all ${sort === 'newest' ? 'bg-background text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                            >
                                <Clock className="w-4 h-4" />
                                Fresh
                            </button>
                        </div>

                        {/* Time Toggles (Only show if NOT Trending, as Trending forces 7d logic usually) */}
                        {sort !== 'trending' && (
                            <div className="flex items-center gap-2 text-sm">
                                <span className="text-muted-foreground mr-2 hidden md:inline">Timeframe:</span>
                                {['24h', '7d', '30d', 'all'].map((t) => (
                                    <button
                                        key={t}
                                        onClick={() => setTimeRange(t)}
                                        className={`px-3 py-1.5 rounded-full border transition-all ${timeRange === t ? 'bg-primary/20 border-primary text-primary' : 'border-transparent text-muted-foreground hover:bg-muted'}`}
                                    >
                                        {t === 'all' ? 'All Time' : t.toUpperCase()}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Create CTA */}
                        <Button className="btn-primary ml-auto hidden md:flex" onClick={() => navigate('/gallery')}>
                            Create SubHack
                        </Button>
                    </div>

                    {/* The Grid */}
                    {loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {[...Array(8)].map((_, i) => (
                                <div key={i} className="card-base overflow-hidden">
                                    <Skeleton className="aspect-video" />
                                    <div className="p-3 space-y-2">
                                        <Skeleton className="h-4 w-3/4" />
                                        <Skeleton className="h-3 w-1/2" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : sessions.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {sessions.map((session, index) => (
                                <ViralCard
                                    key={session.id}
                                    session={session}
                                    rank={index + 1}
                                    onShare={handleShare}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-24 bg-card/30 rounded-xl border border-dashed border-border">
                            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                                <TrendingUp className="w-8 h-8 text-muted-foreground" />
                            </div>
                            <h3 className="font-heading text-xl uppercase mb-2">It's Quiet Here...</h3>
                            <p className="text-muted-foreground mb-6">
                                No clips match this criteria yet. Be the first!
                            </p>
                            <Button className="btn-primary" onClick={() => navigate('/gallery')}>
                                Create the First Viral Hit
                            </Button>
                        </div>
                    )}

                </div>
            </div>
        </Layout>
    );
}